import React, { useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import WidgetSkeleton from "../widget/WidgetSkeleton";
import { formatNumber } from "../../utils/formatter";

type Props = {
  timestampNasional: any;
  isLoadingTimestampNasional: boolean;
  weeklyPodOSProgress: any[] | null;
  isLoadingWeeklyPodOSProgress: boolean;
  monthlyPodOSProgress: any[] | null;
  isLoadingMonthlyPodOSProgress: boolean;
};
const Section8 = ({
  timestampNasional,
  isLoadingTimestampNasional,
  weeklyPodOSProgress,
  isLoadingWeeklyPodOSProgress,
  monthlyPodOSProgress,
  isLoadingMonthlyPodOSProgress,
}: Props) => {
  const defaultWeeks = [
    {
      week: { week_number: 1, start: "", end: "" },
      pod: 0,
      pod_invoice: 0,
      pod_totalbyinvoice: 0,
      stpod: 0,
      stpod_inv: 0,
      stpod_inv_total: 0,
    },
    {
      week: { week_number: 2, start: "", end: "" },
      pod: 0,
      pod_invoice: 0,
      pod_totalbyinvoice: 0,
      stpod: 0,
      stpod_inv: 0,
      stpod_inv_total: 0,
    },
    {
      week: { week_number: 3, start: "", end: "" },
      pod: 0,
      pod_invoice: 0,
      pod_totalbyinvoice: 0,
      stpod: 0,
      stpod_inv: 0,
      stpod_inv_total: 0,
    },
    {
      week: { week_number: 4, start: "", end: "" },
      pod: 0,
      pod_invoice: 0,
      pod_totalbyinvoice: 0,
      stpod: 0,
      stpod_inv: 0,
      stpod_inv_total: 0,
    },
  ];
  const mergedWeeklyPodOSProgress = [
    ...(weeklyPodOSProgress || []),
    ...defaultWeeks,
  ].slice(0, 4);

  const getRoundFactor = (num: number) => {
    if (num <= 100) return 10;
    if (num <= 1000) return 100;
    if (num <= 5000) return 500;
    if (num <= 20000) return 1000;
    if (num <= 50000) return 5000;
    if (num <= 100000) return 10000;
    if (num <= 200000) return 20000;
    return 50000;
  };

  const maxYValue = (() => {
    const data = timestampNasional?.series || [];
    const max = Math.max(...data, 0);
    const roundFactor = getRoundFactor(max);
    return Math.ceil(max / roundFactor) * roundFactor;
  })();

  const maxPodYValue = (() => {
    const allValues = monthlyPodOSProgress
      ? monthlyPodOSProgress.flatMap((d) => [
          d.pod_currentmonth,
          d.pod_prev_currentmonth,
          d.pod_podr,
          d.pod_prev_podr,
          d.pod_podr_tidak_lengkap,
          d.pod_outstanding,
          d.pod_prev_outstanding,
        ])
      : [0];
    const max = Math.max(...allValues, 0);
    const roundFactor = getRoundFactor(max);
    return Math.ceil(max / roundFactor) * roundFactor;
  })();
  // Time Stamp Check Point Chart Options
  const timeStampOptions: ApexOptions = {
    series: [
      {
        name: "Performance",
        data: timestampNasional ? timestampNasional.series : Array(12).fill(0),
      },
    ],
    chart: { type: "bar" as const, height: 300, toolbar: { show: false } },
    colors: ["#b58a58"],
    plotOptions: { bar: { columnWidth: "15px" } },
    dataLabels: { enabled: false },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: "top",
      labels: { colors: "#000000" },
      markers: { size: 12 },
    },
    xaxis: {
      categories: timestampNasional
        ? timestampNasional.labels
        : [
            "JAN",
            "FEB",
            "MAR",
            "APR",
            "MAY",
            "JUN",
            "JUL",
            "AUG",
            "SEP",
            "OCT",
            "NOV",
            "DEC",
          ],
      labels: { style: { colors: "#000000", fontSize: "10px" } },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: (val: number) => {
          return formatNumber(Math.round(val)) + "%";
        },
        style: { colors: "#000000" },
      },
    },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
  };

  // POD Status Chart Options
  const podStatusOptions: ApexOptions = {
    series: [
      {
        name: "Current Month",
        group: "valueyears",
        color: "#fecea8",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_currentmonth)
          : Array(12).fill(0),
      },
      {
        name: "Previous Years",
        group: "valueyears",
        color: "#2b373d",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_prev_currentmonth)
          : Array(12).fill(0),
      },
      {
        name: "Actual Received",
        group: "valueactual",
        color: "#99b898",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_podr)
          : Array(12).fill(0),
      },
      {
        name: "Actual Received fr Previous Years",
        group: "valueactual",
        color: "#5c7964",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_prev_podr)
          : Array(12).fill(0),
      },
      {
        name: "w/ SJ Not Completed",
        group: "valueactual",
        color: "#e84a5f",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_podr_tidak_lengkap)
          : Array(12).fill(0),
      },
      {
        name: "Outstanding",
        group: "valueoutstanding",
        color: "#b48958",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_outstanding)
          : Array(12).fill(0),
      },
      {
        name: "Outstanding fr Previous Years",
        group: "valueoutstanding",
        color: "#d5c188",
        data: monthlyPodOSProgress
          ? monthlyPodOSProgress.map((d) => d.pod_prev_outstanding)
          : Array(12).fill(0),
      },
    ],
    chart: {
      type: "bar" as const,
      height: 300,
      stacked: true,
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "80%" },
    },
    xaxis: {
      categories: [
        "JAN",
        "FEB",
        "MAR",
        "APR",
        "MAY",
        "JUN",
        "JUL",
        "AUG",
        "SEP",
        "OCT",
        "NOV",
        "DEC",
      ],
      labels: { style: { colors: "#000000" } },
    },
    yaxis: {
      min: 0,
      max: maxPodYValue,
      forceNiceScale: true,
      labels: {
        formatter: (val: number) => formatNumber(Math.round(val)),
        style: { colors: "#000000" },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      labels: { colors: "#000000" },
      markers: { size: 12 },
    },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
  };

  return (
    <div className="row mb-3">
      {/* Time Stamp Check Point Card */}
      <div className="mb-2 col-12 col-sm-12 col-md-6 col-lg-3">
        <div className="card bg-white p-4">
          {isLoadingTimestampNasional && <WidgetSkeleton />}
          <div className="text-gray-500 text-md mb-3">
            <span className="text-black fs-sm">
              NATIONAL TIME STAMP CHECK POINT
            </span>
          </div>
          <div
            className="flex justify-between"
            style={{ height: "400px", width: "100%" }}
          >
            <div style={{ width: "100%" }}>
              <ReactApexChart
                options={timeStampOptions}
                series={timeStampOptions.series}
                type="bar"
                height={400}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Monthly POD Status Card */}
      <div className="mb-2 col-12 col-sm-12 col-md-12 col-lg-6">
        <div className="card bg-white p-3 p-sm-4">
          {isLoadingMonthlyPodOSProgress && <WidgetSkeleton />}
          <div className="row mb-2">
            <div className="col-12">
              <div className="flex justify-between">
                <span className="text-black text-sm text-md-base">
                  MONTHLY POD STATUS PROGRESS (incl. uncollected on previous
                  years)
                </span>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div
                style={{
                  position: "relative",
                  height: "400px",
                  minHeight: "400px",
                  maxHeight: "400px",
                }}
              >
                <ReactApexChart
                  options={podStatusOptions}
                  series={podStatusOptions.series}
                  type="bar"
                  height={400}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly POD Outstanding Card */}
      <div className="mb-2 col-12 col-sm-12 col-md-6 col-lg-3">
        <div className="card bg-white p-3 p-sm-4">
          {isLoadingWeeklyPodOSProgress && <WidgetSkeleton />}
          <div className="row mb-2">
            <div className="col-12">
              <div className="flex justify-between">
                <span className="text-black text-sm text-md-base">
                  WEEKLY POD OUTSTANDING PROGRESS
                </span>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div
                className="table-responsive"
                style={{ height: "400px", overflowY: "auto" }}
              >
                <table className="table table-sm" style={{ fontSize: "10px" }}>
                  <thead>
                    <tr>
                      <th
                        className="d-none d-sm-table-cell"
                        style={{
                          width: "20%",
                          backgroundColor: "#4a0f0f",
                          color: "white",
                        }}
                      ></th>
                      <th
                        className="text-nowrap"
                        style={{
                          width: "10%",
                          backgroundColor: "#4a0f0f",
                          color: "white",
                        }}
                      >
                        Outstanding POD
                      </th>
                      <th
                        className="d-none d-md-table-cell"
                        style={{
                          width: "30%",
                          backgroundColor: "#4a0f0f",
                          color: "white",
                        }}
                      >
                        POD minus SJ
                      </th>
                      <th
                        className="d-none d-lg-table-cell"
                        style={{
                          width: "18%",
                          backgroundColor: "#4a0f0f",
                          color: "white",
                        }}
                      >
                        # Invoice
                      </th>
                      <th
                        className="text-nowrap"
                        style={{
                          width: "22%",
                          backgroundColor: "#4a0f0f",
                          color: "white",
                        }}
                      >
                        Amount of Invoices
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {mergedWeeklyPodOSProgress.map((weekData, idx) => (
                      <React.Fragment key={idx}>
                        <tr style={{ height: "10px" }}>
                          <td
                            className="d-none d-sm-table-cell"
                            style={{
                              verticalAlign: "middle",
                              padding: "2px",
                            }}
                          >
                            Week {weekData.week.week_number}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                        <tr
                          style={{
                            height: "10px",
                            border: "1px solid #dee2e6",
                            borderBottom: "none",
                          }}
                        >
                          <td
                            className="d-none d-sm-table-cell"
                            style={{
                              backgroundColor: "#40a36d",
                              color: "white",
                              textAlign: "center",
                              verticalAlign: "middle",
                              padding: "2px",
                            }}
                          >
                            POD-P
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {weekData.pod}
                          </td>
                          <td
                            className="d-none d-md-table-cell"
                            style={{ textAlign: "center" }}
                          >
                            {weekData.pod - weekData.pod_invoice}
                          </td>
                          <td
                            className="d-none d-lg-table-cell"
                            style={{ textAlign: "center" }}
                          >
                            {weekData.pod_invoice}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {formatNumber(weekData.pod_totalbyinvoice)}
                          </td>
                        </tr>
                        <tr
                          style={{
                            height: "10px",
                            border: "1px solid #dee2e6",
                            borderTop: "none",
                          }}
                        >
                          <td
                            className="d-none d-sm-table-cell"
                            style={{
                              backgroundColor: "#df6991",
                              color: "white",
                              textAlign: "center",
                              verticalAlign: "middle",
                              padding: "2px",
                            }}
                          >
                            STPOD
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {weekData.stpod}
                          </td>
                          <td
                            className="d-none d-md-table-cell"
                            style={{ textAlign: "center" }}
                          >
                            {weekData.stpod - weekData.stpod_inv}
                          </td>
                          <td
                            className="d-none d-lg-table-cell"
                            style={{ textAlign: "center" }}
                          >
                            {weekData.stpod_inv}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {formatNumber(weekData.stpod_inv_total)}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Section8;
